<template>
  <div class="invoices-wrapper">
    <!-- Header -->
    <div class="receipt-header-container">
      <div class="receipt-logo">BNC</div>
      <div v-if="customer_name" class="receipt-customer">
        Customer: {{ customer_name }}
      </div>
    </div>
    <div class="mt-4 mb-4">
      <button style="background:#202133; color:white; padding:5px 15px; &:hover {cursor: pointer;}" @click="goToLogScreen()">
        Back to Log Screen
      </button>
    </div>
    <!-- Invoice List -->
    <div class="invoice-list">
      <div v-if="invoices.length === 0" class="empty-state">
        <p>No invoices found for this customer.</p>
      </div>

      <div v-else>
        <div v-for="(invoice, index) in invoices" :key="invoice.invoice_id" class="invoice-item">
          <div
              @click="toggleInvoice(index)"
              class="invoice-summary"
              :class="{'active': expandedInvoices[index]}"
          >
            <div class="invoice-id">#{{ invoice.invoice_id }}</div>
            <div class="invoice-date">{{ formatDate(invoice.order_lines && invoice.order_lines.length > 0 ? invoice.order_lines[0].first_created_at : null) }}</div>
            <div class="invoice-total">{{ formatCurrency(invoice.total) }}</div>
            <div class="expand-icon">{{ expandedInvoices[index] ? '▼' : '►' }}</div>
          </div>

          <!-- Receipt Detail -->
          <transition name="fade">
            <div v-if="expandedInvoices[index]" class="receipt-container">
              <div class="receipt">
                <!-- Receipt Header -->
                <div class="receipt-title">
                  <div class="store-name">BNC</div>
                  <div class="invoice-number">INVOICE #{{ invoice.invoice_id }}</div>
                  <div class="receipt-date">{{ formatDate(invoice.order_lines && invoice.order_lines.length > 0 ? invoice.order_lines[0].first_created_at : null, true) }}</div>
                  <div class="server-name">Served By: {{ staff_name }}</div>
                  <div class="customer-name">Customer: {{ customer_name }}</div>
                </div>

                <div class="receipt-divider">************************************</div>

                <!-- Line Items -->
                <div v-if="invoice.order_lines.length === 0" class="no-items">
                  No items in this invoice
                </div>

                <div v-else class="line-items">
                  <div v-for="(item, itemIndex) in invoice.order_lines" :key="itemIndex" class="item">
                    <div class="item-row">
                      <div class="item-name">{{ formatProductName(item.product_name) }}</div>
                      <div class="item-spacer"></div>
                      <div class="item-price">{{ formatCurrency(item.price) }}</div>
                    </div>

                    <div class="item-qty">× {{ item.qty }}</div>

                    <!-- Extra items from tags -->
                    <div v-if="item.tags" class="extras">
                      <div v-for="(extra, extraIndex) in parseExtras(item.tags)" :key="extraIndex" class="extra-row">
                        <div class="extra-name">+ {{ extra.name }}</div>
                        <div class="item-spacer"></div>
                        <div class="extra-price">{{ formatCurrency(extra.price) }}</div>
                      </div>
                    </div>

                    <!-- Options -->
                    <div v-if="item.options" class="options">
                      <div v-for="(option, optIndex) in parseOptions(item.options)" :key="optIndex" class="option">
                        « {{ option }} »
                      </div>
                    </div>
                  </div>
                </div>

                <div class="receipt-divider">************************************</div>

                <!-- Totals Section -->
                <div class="totals">
                  <div class="total-row">
                    <div class="total-label">TOTAL</div>
                    <div class="total-spacer"></div>
                    <div class="total-amount">{{ formatCurrency(invoice.total) }}</div>
                  </div>
                </div>

                <!-- Receipt Footer -->
                <div class="receipt-footer">
                  <div>Thank you for your business!</div>
                  <div>Visit us again soon</div>
                </div>
              </div>

              <!-- Print Buttons -->
              <div class="print-section">
                <button @click="printInvoice(invoice)" class="print-button">
                  <i class="print-icon">🖨️</i> Print Receipt
                </button>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
    <!-- Snackbar for notifications -->
    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        bottom
        right
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
            text
            v-bind="attrs"
            @click="snackbar.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'Invoices',
  data: function() {
    return {
      customer_id: null,
      customer_name: '', // Replace with actual data from API
      staff_name: '', // Replace with actual data from API
      invoices: [],
      expandedInvoices: {},
      staff_id: null,
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000
      }
    };
  },
  created: function() {
    // Get language preference
    var language_id = localStorage.getItem('language_id');
    console.log('lang: ' + language_id);
    var language = 'en';
    if (language_id) {
      language = language_id;
    } else {
      var chinese_staff_ids = [3, 7, 8];
      if (chinese_staff_ids.indexOf(this.staff_id) !== -1) {
        language = 'zh';
      }
    }
    this.$i18n.locale = language;

    // Get customer ID from route params
    this.customer_id = parseInt(this.$route.params.customer_id);
    this.getInvoices();
  },
  methods: {
    getInvoices: function() {
      var self = this;
      this.$http.get(
          this.$http.defaults.baseURL + '/staff/invoices/' + this.customer_id + '/'
      ).then(function(response) {
        self.invoices = response.data;
        // Set customer name from first invoice if available
        if (self.invoices.length > 0 && self.invoices[0].customer_name) {
          self.customer_name = self.invoices[0].customer_name;
          self.staff_name = self.invoices[0].staff_name;
        }

        // Initialize all invoices as collapsed except the first one
        for (var i = 0; i < self.invoices.length; i++) {
          self.$set(self.expandedInvoices, i, i === 0); // Set first invoice (index 0) to expanded (true)
        }
      }).catch(function(error) {
        console.error('Error fetching invoices:', error);
      });
    },
    toggleInvoice: function(index) {
      // Close all other invoices
      var self = this;
      Object.keys(this.expandedInvoices).forEach(function(key) {
        if (parseInt(key) !== index) {
          self.$set(self.expandedInvoices, key, false);
        }
      });

      // Toggle the clicked invoice
      this.$set(this.expandedInvoices, index, !this.expandedInvoices[index]);
    },
    formatDate: function(dateString, includeTime) {
      if (!dateString) return 'N/A';
      includeTime = !!includeTime;

      var date = new Date(dateString);
      var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      if (includeTime) {
        return months[date.getMonth()] + ' ' + date.getDate() + ', ' + date.getFullYear() + ' ' +
            ('0' + date.getHours()).slice(-2) + ':' +
            ('0' + date.getMinutes()).slice(-2);
      } else {
        return months[date.getMonth()] + ' ' + date.getDate() + ', ' + date.getFullYear();
      }
    },
    formatCurrency: function(amount) {
      if (amount === null || amount === undefined) return '¥0.00';
      return '¥' + parseFloat(amount).toFixed(2);
    },
    goToLogScreen() {
      this.$router.push({path: '/c-panel/staff'})
    },
    formatProductName: function(name) {
      return name;
    },
    parseExtras: function(tags) {
      if (!tags) return [];

      var extras = [];
      var tagPairs = tags.split(',');

      for (var i = 0; i < tagPairs.length; i++) {
        var pair = tagPairs[i];
        var parts = pair.split(':');
        var name = parts[0];
        var priceStr = parts[1];

        if (name && priceStr) {
          extras.push({
            name: name.trim(),
            price: parseFloat(priceStr.trim())
          });
        }
      }

      return extras;
    },
    parseOptions: function(options) {
      if (!options) return [];

      var parsedOptions = [];
      var optionPairs = options.split(',');

      for (var i = 0; i < optionPairs.length; i++) {
        var pair = optionPairs[i];
        var trimmedPair = pair.trim();
        var match = trimmedPair.match(/(\d+):\s*(.*)/);

        if (match) {
          var description = match[2];
          parsedOptions.push(description.trim());
        } else {
          parsedOptions.push(trimmedPair);
        }
      }

      return parsedOptions;
    },
    printInvoice: function(invoice) {
      // Prepare the payload with invoice data and receipt format
      const payload = {
        print_type: 'RECEIPT',
        invoice_data: {
          // Invoice metadata
          invoice_id: invoice.invoice_id,
          customer_name: this.customer_name,
          staff_name: this.staff_name,
          date: invoice.order_lines && invoice.order_lines.length > 0 ?
              invoice.order_lines[0].first_created_at : null,
          total: invoice.total,

          // Store info
          store_name: 'BNC',

          // Line items with all details
          items: invoice.order_lines.map(item => ({
            product_name: item.product_name,
            price: item.price,
            qty: item.qty,
            line_total: item.price * item.qty,

            // Parse extras from tags
            extras: this.parseExtras(item.tags),

            // Parse options
            options: this.parseOptions(item.options)
          })),

          // Calculated totals
          subtotal: invoice.total,
          total_amount: invoice.total
        },

        // Additional metadata for the print queue
        timestamp: new Date().toISOString(),
        source: 'staff_terminal'
      };

      // Show loading state
      this.showInfoSnackbar('Sending receipt to printer...');

      // Send to your Django API
      this.$http.post(
          this.$http.defaults.baseURL +'/printer/send',
          payload
      ).then((response) => {
        console.log('Print response:', response.data);

        // Handle success response
        if (response.data.success) {
          this.showSuccessSnackbar(response.data.snackbar_message || 'Receipt sent to printer successfully!');
        } else {
          this.showErrorSnackbar(response.data.snackbar_message || 'Failed to send receipt to printer');
        }
      }).catch((error) => {
        console.error('Print error:', error);

        // Handle different error scenarios
        if (error.response && error.response.data && error.response.data.snackbar_message) {
          this.showErrorSnackbar(error.response.data.snackbar_message);
        } else if (error.response && error.response.status === 403) {
          this.showErrorSnackbar('Authentication failed - invalid API key');
        } else if (error.response && error.response.status === 400) {
          this.showErrorSnackbar('Invalid request - check print data');
        } else {
          this.showErrorSnackbar('Network error - could not connect to printer service');
        }
      });
    },

// Vuetify 2 Snackbar methods
    showSuccessSnackbar: function(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'success',
        timeout: 4000
      };
    },

    showErrorSnackbar: function(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'error',
        timeout: 6000
      };
    },

    showInfoSnackbar: function(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'info',
        timeout: 3000
      };
    }
  }
};
</script>

<style scoped>
/* Overall container */
.invoices-wrapper {
  max-width: 500px;
  margin: 0 auto;
  padding: 20px;
  font-family: 'Courier New', monospace;
}

/* Header styling */
.receipt-header-container {
  text-align: center;
  margin-bottom: 30px;
}

.receipt-logo {
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 5px;
}

.receipt-customer {
  font-size: 16px;
}

/* Invoice list styling */
.invoice-list {
  border-radius: 4px;
  overflow: hidden;
}

.invoice-item {
  margin-bottom: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.invoice-summary {
  display: flex;
  justify-content: space-between;
  padding: 12px 15px;
  background-color: #f5f5f5;
  cursor: pointer;
  font-size: 14px;
  border-bottom: 1px dashed #ccc;
  transition: background-color 0.2s;
}

.invoice-summary:hover {
  background-color: #eaeaea;
}

.invoice-summary.active {
  background-color: #e0e0e0;
  font-weight: bold;
}

.invoice-id {
  width: 80px;
}

.invoice-date {
  flex-grow: 1;
  text-align: center;
}

.invoice-total {
  width: 80px;
  text-align: right;
}

.expand-icon {
  width: 20px;
  text-align: center;
  margin-left: 10px;
}

/* Receipt styling */
.receipt-container {
  padding: 0;
  overflow: hidden;
}

.receipt {
  background-color: white;
  padding: 25px 15px;
  width: 100%;
  box-sizing: border-box;
  font-size: 13px;
  line-height: 1.3;
}

.receipt-title {
  text-align: center;
  margin-bottom: 15px;
}

.store-name {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 5px;
}

.invoice-number, .receipt-date, .customer-name {
  margin-bottom: 3px;
}

.receipt-divider {
  overflow: hidden;
  white-space: nowrap;
  margin: 10px 0;
  font-size: 12px;
  letter-spacing: -1px;
}

.no-items {
  text-align: center;
  font-style: italic;
  padding: 10px 0;
}

/* Line items styling */
.line-items {
  margin: 15px 0;
}

.item {
  margin-bottom: 12px;
}

.item-row, .extra-row, .total-row {
  display: flex;
  width: 100%;
}

.item-name, .extra-name, .total-label {
  flex-grow: 1;
}

.item-spacer, .total-spacer {
  flex-grow: 1;
  border-bottom: 1px dotted #ccc;
  height: 1em;
  margin: 0 5px;
}

.item-price, .extra-price, .total-amount {
  text-align: right;
  white-space: nowrap;
}

.item-qty {
  padding-left: 20px;
  font-size: 12px;
  color: #555;
  margin-top: 2px;
}

.extras {
  padding-left: 20px;
  margin-top: 3px;
}

.extra-row {
  margin-top: 2px;
  font-size: 12px;
}

.options {
  padding-left: 20px;
  margin-top: 4px;
  font-size: 12px;
  font-style: italic;
  color: #555;
}

/* Totals section */
.totals {
  margin-top: 10px;
  font-weight: bold;
}

/* Receipt footer */
.receipt-footer {
  text-align: center;
  margin-top: 20px;
  font-size: 12px;
  color: #555;
}

/* Print button styling */
.print-section {
  text-align: center;
  margin: 15px 0;
  padding: 0 15px 15px;
  display: flex;
  gap: 10px;
  justify-content: center;
}

.print-button {
  background-color: #2c3e50;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  flex: 1;
  max-width: 150px;
}

.print-button:hover {
  background-color: #1a2530;
}

.thermal-button {
  background-color: #e67e22;
}

.thermal-button:hover {
  background-color: #d35400;
}

.print-icon {
  margin-right: 5px;
}

/* Animation */
.fade-enter-active, .fade-leave-active {
  transition: all 0.3s;
  max-height: 2000px;
  opacity: 1;
  overflow: hidden;
}

.fade-enter, .fade-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 30px;
  color: #777;
  font-style: italic;
}

/* Print-specific styles */
@media print {
  .invoice-summary, .print-section {
    display: none;
  }

  .invoices-wrapper {
    padding: 0;
  }

  .receipt {
    padding: 0;
    border: none;
  }
}
</style>